import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { AdminProvider } from './contexts/AdminContext';
import { useScrollToTop } from './hooks/useScrollToTop';

// Eager load core pages for fast initial render
import PersonalLanding from './pages/PersonalLanding';

// Lazy load other pages for better code splitting
const Solutions = lazy(() => import('./pages/Solutions'));
const Contact = lazy(() => import('./pages/Contact'));
const Success = lazy(() => import('./pages/Success'));
const Cancel = lazy(() => import('./pages/Cancel'));

// Portfolio pages (heavier - contain markdown/code highlighting)
const Portfolio = lazy(() => import('./pages/Portfolio'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const RequestAccess = lazy(() => import('./pages/RequestAccess'));
const CodeViewer = lazy(() => import('./pages/CodeViewer'));

// Special pages
const TreasuryDemo = lazy(() => import('./pages/TreasuryDemo'));
const GreenLightPDF = lazy(() => import('./pages/GreenLightPDF'));


// Admin pages
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminRequests = lazy(() => import('./pages/admin/Requests'));
const AdminProjects = lazy(() => import('./pages/admin/Projects'));

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
        <p className="text-gray-400 text-sm">Loading...</p>
      </div>
    </div>
  );
}

function ScrollToTop() {
  useScrollToTop();
  return null;
}

// Layout wrapper that conditionally shows Navbar/Footer
function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isCodeRoute = location.pathname.startsWith('/code');
  const isHomePage = location.pathname === '/';
  const isTreasuryDemo = location.pathname === '/the-green-light';
  const isGreenLightPDF = location.pathname === '/the-green-light/pdf';

  if (isAdminRoute || isCodeRoute || isHomePage || isTreasuryDemo || isGreenLightPDF) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-grow">{children}</div>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AdminProvider>
        <ScrollToTop />
        <Layout>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<PersonalLanding />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/contact" element={<Contact />} />

              {/* Portfolio Routes */}
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/portfolio/:slug" element={<ProjectDetail />} />
              <Route path="/request-access" element={<RequestAccess />} />

              {/* Code Viewer Routes */}
              <Route path="/code" element={<CodeViewer />} />
              <Route path="/code/:projectId" element={<CodeViewer />} />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/requests" element={<AdminRequests />} />
              <Route path="/admin/projects" element={<AdminProjects />} />

              {/* Special Demo Routes */}
              <Route path="/the-green-light" element={<TreasuryDemo />} />
              <Route path="/the-green-light/pdf" element={<GreenLightPDF />} />

              {/* Other Routes */}
              <Route path="/success" element={<Success />} />
              <Route path="/cancel" element={<Cancel />} />
            </Routes>
          </Suspense>
        </Layout>
      </AdminProvider>
    </Router>
  );
}

export default App;
